/* @flow */

/**
 * This enables the use of multiple mixins that provide static `onEnter` methods to be
 * used with `react-router` views.
 *
 * This is used by providing an array of mixins like so:
 *
 *   static onEnter = requireOnEnter([RequireUser])
 *
 * The order matters. chainOnEnter will skip any remaining onEnters once replaceWith is called.
 *
 * You must specify the onEnter method in the route definition, or it will never get called:
 *
 *   <Route
 *     path="/chat/leave/:token"
 *     component={LeaveChatView}
 *     onEnter={LeaveChatView.onEnter}
 *    />
 */

import type { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';

const ASYNC_ARITY = 3;

// returns a fn for react-route onEnter static prop.
export default function chainOnEnter(mixins: Array<any>) {
  return function(nextState: RouterState, replaceWith: (location: LocationDescriptor) => void, routerCallback: Function) {
    let i = 0;
    let replaceCalled = false;
    const replaceOnce = (loc) => {
      replaceCalled = true;
      replaceWith(loc);
    };
    const recursiveCb = function() {
      if (i < mixins.length && !replaceCalled) {
        const nextOnEnter = mixins[i++].statics.onEnter;
        if (nextOnEnter.length < ASYNC_ARITY) {
          nextOnEnter(nextState, replaceOnce);
          recursiveCb();
        } else {
          nextOnEnter(nextState, replaceOnce, recursiveCb);
        }
      } else {
        routerCallback();
      }
    };
    recursiveCb();
  };
}



// WEBPACK FOOTER //
// ./src/components/lib/chainOnEnter.js